import React, { useEffect, useRef, useState } from 'react';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.css';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import useToken from '../App/useToken';
import '../UserResponsive.css';

function SpecialPredictions() {
    const { token } = useToken();
    const toast = useRef(null);
    const [predictions, setPredictions] = useState([]);
    const [semifinalists, setSemifinalists] = useState(['', '', '', '']);
    const [officialSemifinalistsText, setOfficialSemifinalistsText] = useState('');
    const [semifinalistsPoints, setSemifinalistsPoints] = useState(0);
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [deadline, setDeadline] = useState(null);
    const [totalSpecialPoints, setTotalSpecialPoints] = useState(0);

    useEffect(() => {
        loadPredictions();
    }, []);

    const authHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + token,
    };

    const loadPredictions = () => {
        fetch(process.env.REACT_APP_URL + 'special-predictions', {
            method: 'GET',
            headers: authHeaders,
        })
            .then((response) => response.json())
            .then((data) => {
                setPredictions(data.predicciones || []);
                setTeams(data.teams || []);
                setPlayers(data.players || []);
                setIsOpen(Boolean(data.abiertas));
                setDeadline(data.deadline);
                setTotalSpecialPoints(data.puntos_especiales_total || 0);

                const selected = data.semifinalistas && data.semifinalistas.selected_team_ids ? data.semifinalistas.selected_team_ids : [];
                setSemifinalists([selected[0] || '', selected[1] || '', selected[2] || '', selected[3] || '']);
                setOfficialSemifinalistsText(data.semifinalistas ? data.semifinalistas.resultado_texto || '' : '');
                setSemifinalistsPoints(data.semifinalistas ? data.semifinalistas.puntos || 0 : 0);
            });
    };

    const onPredictionChange = (categoryId, field, value) => {
        setPredictions(predictions.map((prediction) => {
            if (prediction.category_id !== categoryId) {
                return prediction;
            }

            return {
                ...prediction,
                selected_team_id: field === 'selected_team_id' ? value : null,
                selected_player_id: field === 'selected_player_id' ? value : null,
            };
        }));
    };

    const onSemifinalistChange = (index, value) => {
        const next = [...semifinalists];
        next[index] = value;
        setSemifinalists(next);
    };

    const teamOptionTemplate = (option) => {
        if (!option) {
            return null;
        }

        return (
            <div className="team-cell">
                <img src={process.env.REACT_APP_URL_IMAGES + option.bandera} onError={(e) => e.target.src='https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png'} alt={option.nombre} className="product-image" />
                <span>{option.nombre}</span>
            </div>
        );
    };

    const playerOptionTemplate = (option) => {
        if (!option) {
            return null;
        }

        return (
            <div className="team-cell">
                <img src={process.env.REACT_APP_URL_IMAGES + option.bandera} onError={(e) => e.target.src='https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png'} alt={option.team_name} className="product-image" />
                <span>{option.name} - {option.team_name}</span>
            </div>
        );
    };

    const getTeamOptionsForSemifinalist = (index) => {
        const selected = semifinalists.filter((teamId, selectedIndex) => teamId && selectedIndex !== index).map((teamId) => Number(teamId));
        return teams.filter((team) => !selected.includes(Number(team.id)));
    };

    const getPlayerOptions = (prediction) => {
        if (prediction.tipo === 'goalkeeper') {
            return players.filter((player) => player.position === 'portero');
        }

        return players.filter((player) => player.position === 'jugador');
    };

    const savePredictions = () => {
        const selectedSemifinalists = semifinalists.filter(Boolean);
        const uniqueSemifinalists = [...new Set(selectedSemifinalists.map((teamId) => Number(teamId)))];

        if (selectedSemifinalists.length !== 4 || uniqueSemifinalists.length !== 4) {
            toast.current.show({ severity: 'error', summary: 'Semifinalistas', detail: 'Debes seleccionar 4 semifinalistas diferentes', life: 4000 });
            return;
        }

        fetch(process.env.REACT_APP_URL + 'special-predictions', {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({ predicciones: predictions, semifinalistas: uniqueSemifinalists }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.code === 200) {
                    toast.current.show({ severity: 'success', summary: 'Predicciones', detail: data.mensaje, life: 3000 });
                    loadPredictions();
                }
                else {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: data.mensaje || 'No se pudo guardar', life: 4000 });
                }
            });
    };

    const renderPredictionInput = (prediction) => {
        if (prediction.tipo === 'team') {
            return (
                <Dropdown
                    value={prediction.selected_team_id || null}
                    options={teams}
                    optionLabel="nombre"
                    optionValue="id"
                    itemTemplate={teamOptionTemplate}
                    valueTemplate={teamOptionTemplate}
                    disabled={!isOpen}
                    placeholder="Selecciona una seleccion"
                    filter
                    onChange={(e) => onPredictionChange(prediction.category_id, 'selected_team_id', e.value)}
                />
            );
        }

        return (
            <Dropdown
                value={prediction.selected_player_id || null}
                options={getPlayerOptions(prediction)}
                optionLabel="name"
                optionValue="id"
                itemTemplate={playerOptionTemplate}
                valueTemplate={playerOptionTemplate}
                disabled={!isOpen}
                placeholder={prediction.tipo === 'goalkeeper' ? 'Selecciona un portero' : 'Selecciona un jugador'}
                filter
                onChange={(e) => onPredictionChange(prediction.category_id, 'selected_player_id', e.value)}
            />
        );
    };

    return (
        <div className="card mb-4">
            <Toast ref={toast} />
            <div className="mb-3">
                <Button
                    type="button"
                    label="Predicciones especiales"
                    icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                    iconPos="right"
                    className="p-button-text special-predictions-toggle"
                    onClick={() => setExpanded(!expanded)}
                />
                <div className="special-predictions-summary mt-2">
                    Semifinalistas, campeon y premios individuales. Total actual: {totalSpecialPoints} puntos.
                </div>
            </div>

            {expanded && (
                <React.Fragment>
                    <div className="text-700 text-sm mb-3">
                        Cierre: {deadline || 'pendiente de configurar'}.
                    </div>

                    <div className="p-fluid grid">
                        {[0, 1, 2, 3].map((index) => (
                            <div className="field col-12 md:col-6" key={`semifinalista-${index}`}>
                                <label htmlFor={`semifinalista-${index}`}>Semifinalista {index + 1}</label>
                                <Dropdown
                                    id={`semifinalista-${index}`}
                                    value={semifinalists[index] || null}
                                    options={getTeamOptionsForSemifinalist(index)}
                                    optionLabel="nombre"
                                    optionValue="id"
                                    itemTemplate={teamOptionTemplate}
                                    valueTemplate={teamOptionTemplate}
                                    disabled={!isOpen}
                                    placeholder="Selecciona una seleccion"
                                    filter
                                    onChange={(e) => onSemifinalistChange(index, e.value)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="text-700 text-sm mb-4">
                        Resultado oficial semifinalistas: {officialSemifinalistsText || 'pendiente'} | Puntos: {semifinalistsPoints}
                    </div>

                    <div className="p-fluid grid">
                        {predictions.map((prediction) => (
                            <div className="field col-12 md:col-6" key={prediction.category_id}>
                                <label htmlFor={`prediction-${prediction.category_id}`}>{prediction.nombre}</label>
                                {renderPredictionInput(prediction)}
                                <small className="text-700">Resultado oficial: {prediction.resultado_texto || 'pendiente'}</small>
                                <br />
                                <small className="text-700">Puntos de esta prediccion: {prediction.puntos}</small>
                            </div>
                        ))}
                    </div>

                    {isOpen ? (
                        <Button label="Guardar predicciones" icon="pi pi-save" className="p-button-success" onClick={savePredictions} />
                    ) : (
                        <span className="text-700 text-sm">Las predicciones especiales estan cerradas.</span>
                    )}
                </React.Fragment>
            )}
        </div>
    );
}

export default SpecialPredictions;
